/**
 * How long this will take, and whether a smaller version is worth offering
 * (D-912).
 *
 * `asked`, the owner: *"notifies the user when they are about to try and
 * upload a large file over cellular… basically flag that if they want the
 * video to stay in higher quality then they need to do it over wifi or with
 * faster cellular."*
 *
 * **Pure on purpose.** The arithmetic is where this is easy to get quietly
 * wrong — an estimate that forgets the encode, or one that offers a saving on
 * a connection fast enough not to need it — and none of that needs a browser
 * to test.
 */

import { expectedBytesAfter } from './videoTarget'

/*
 * **How big a smaller copy comes out is computed, not guessed** — see
 * `expectedBytesAfter` in `./videoTarget`, which reads the same target the
 * encoder aims at.
 *
 * ⚠ This was `EXPECT_SHRINK_TO = 1 / 3`, a flat assumption that the copy comes
 * out a third the size, and it was **wrong by 1.95x** on the first real clip it
 * was ever measured against. Keeping it here also meant the quality dial could
 * be turned in `videoTarget` while this file went on quoting waits for the old
 * one.
 */

/**
 * How fast this device is expected to encode, as a multiple of realtime.
 *
 * ⚠ **Also a guess, and deliberately a pessimistic one.** Desktop Edge managed
 * about 12x realtime with hardware acceleration; a phone falling back to a
 * software encoder can run *below* realtime. Quoting a wait we then beat is
 * recoverable; quoting one we miss is the thing that makes somebody stop
 * trusting the number. The encode is abandoned if it overruns anyway (D-903),
 * so the floor here only has to be honest enough to decide with.
 */
const EXPECT_ENCODE_SPEED = 2

/**
 * Below this there is nothing worth interrupting her for.
 *
 * A prompt is a real cost — it arrives mid-task, on a phone, while she is
 * trying to send something — so it has to buy more than a few seconds.
 */
const WORTH_ASKING_SECONDS = 45

export type UploadForecast = {
  /** Seconds to send what she picked, at the speed actually measured. */
  asIs: number
  /** Seconds to encode and then send a smaller copy. */
  smaller: number
  /** Seconds of upload a smaller copy would save, for the encode deadline. */
  savedByShrinking: number
  /**
   * Whether to put the choice in front of her at all.
   *
   * False on a fast connection, on a short upload, and whenever shrinking
   * would not actually come out ahead — which is the whole reason this is
   * computed from a measurement rather than from which radio she is on.
   */
  worthOffering: boolean
}

export function forecastUpload(
  bytes: number,
  bytesPerSecond: number,
  durationSeconds: number,
  /**
   * What has already reached storage, so `asIs` is the wait she actually has
   * left rather than the one she started with.
   *
   * ⚠ **Not cosmetic.** The speed is measured a full round of blocks in, so on
   * a small-but-slow upload most of the file can be gone by the time there is
   * anything to offer — and shrinking **restarts from zero**. Forecasting from
   * the whole file there quotes a saving that is really a loss: 20 MB at
   * 250 KB/s with 16 MB confirmed has **sixteen seconds left**, against ten to
   * encode and fifty to send the copy from the beginning.
   */
  alreadySent = 0,
): UploadForecast {
  if (!(bytesPerSecond > 0) || !(bytes > 0)) {
    return { asIs: 0, smaller: 0, savedByShrinking: 0, worthOffering: false }
  }

  const asIs = Math.max(bytes - alreadySent, 0) / bytesPerSecond
  const expected = expectedBytesAfter(bytes, durationSeconds)

  /*
   * **No prediction, no offer — but the wait is still the wait.** Three things
   * mean no prediction: no usable running time, a clip too short to believe,
   * and one already under the leave-alone floor, which the encoder would hand
   * straight back.
   *
   * ⚠ **`asIs` is reported anyway, and that is deliberate.** It does not depend
   * on the duration at all, so zeroing it would be a missing lookup rendering
   * as a statement about the data (D-209) — and this function is the only place
   * that knows how long the upload has left.
   */
  if (expected === null) {
    return { asIs, smaller: 0, savedByShrinking: 0, worthOffering: false }
  }

  /*
   * **The whole copy, because a smaller one goes up from the beginning.** The
   * reservation takes exactly one write (D-751), so the bytes already confirmed
   * are abandoned with the attempt that sent them.
   */
  const sendingSmaller = expected / bytesPerSecond

  /*
   * **Upload seconds only** — the encode cannot be part of its own budget. This
   * is what D-903 judges the encode against, so it has to be the time the
   * encode is *buying*.
   */
  const savedByShrinking = asIs - sendingSmaller

  /*
   * **The wait we quote includes the encode**, which is the whole difference
   * between a number she can trust and a number that is technically about the
   * upload. Telling her *"about a minute"* and then spending forty seconds
   * encoding first is a sentence that was never true.
   */
  const encode = durationSeconds > 0 ? durationSeconds / EXPECT_ENCODE_SPEED : 0
  const smaller = encode + sendingSmaller

  return {
    asIs,
    smaller,
    savedByShrinking,
    /*
     * ⚠ **The saving has to clear the same bar as the wait**, not merely be
     * positive. `smaller < asIs` let through a case where the two options
     * rounded to the same words — *"will take about 3 minutes… a smaller copy
     * takes about 3 minutes"* — which is an interruption whose own sentence
     * says it is not worth taking. Reachable whenever the upload runs at about
     * four fifths of the clip's own length.
     */
    worthOffering: asIs >= WORTH_ASKING_SECONDS && asIs - smaller >= WORTH_ASKING_SECONDS,
  }
}

/**
 * "about 4 minutes", "about 40 seconds" — rounded the way a person would say
 * it, because a wait quoted to the second reads as a promise.
 */
export function describeWait(seconds: number): string {
  if (seconds < 20) return 'a few seconds'
  if (seconds < 45) return `about ${Math.round(seconds / 10) * 10} seconds`

  /*
   * ⚠ **A minute is its own case, and it has to be reached before rounding.**
   * The first version switched to minutes at ninety seconds and then said
   * `Math.round(seconds / 60)` — so anything that got there rounded to **two**
   * or more, and *"about a minute"* was a sentence the function could never
   * print. Found by the test written for it, which is the only way a dead
   * branch in wording ever is.
   */
  if (seconds < 90) return 'about a minute'

  return `about ${Math.round(seconds / 60)} minutes`
}
